import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

interface RBACPermissions {
  userEmail: string;
  isSuperAdmin: boolean;
  agentAdminRoles: Array<{
    agentId: string;
    role: string;
    assignedAt: string;
  }>;
  permissions: Array<{
    agentId: string;
    role: string;
    assignedAt: string;
  }>;
}

interface UseRBACPermissionsReturn {
  permissions: RBACPermissions | null;
  isLoading: boolean;
  error: string | null;
  hasAdminAccess: () => boolean;
  hasFullAdminAccess: () => boolean;
  isSuperAdmin: () => boolean;
  isAgentAdmin: (agentId?: string) => boolean;
  canManageAgent: (agentId: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

export const useRBACPermissions = (): UseRBACPermissionsReturn => {
  const { isAuthenticated } = useAuth();
  const [permissions] = useState<RBACPermissions | null>(null);
  const [isLoading] = useState(false); // No longer loading from old API
  const [error] = useState<string | null>(null);

  // NOTE: This hook is deprecated and no longer fetches from the old table-based RBAC system.
  // With Entra RBAC, all permissions come from JWT roles in useAuth().user.roles
  // This hook remains for backward compatibility but returns empty/false values.
  
  const loadPermissions = useCallback(async () => {
    // Deprecated - no-op
  }, []);

  // Load permissions when authentication state changes
  useEffect(() => {
    // Deprecated - no-op
  }, [isAuthenticated]);

  // Check if user has any admin access
  const hasAdminAccess = useCallback(() => {
    if (!permissions) return false;
    return permissions.isSuperAdmin || permissions.agentAdminRoles.length > 0;
  }, [permissions]);

  // Check if user has full admin access (super admin)
  const hasFullAdminAccess = useCallback(() => {
    return permissions?.isSuperAdmin || false;
  }, [permissions]);

  // Check if user is super admin
  const isSuperAdmin = useCallback(() => {
    return permissions?.isSuperAdmin || false;
  }, [permissions]);

  // Check if user is admin for a specific agent
  const isAgentAdmin = useCallback((agentId?: string) => {
    if (!permissions) return false;
    
    // Super admins are admins for all agents
    if (permissions.isSuperAdmin) return true;
    
    // If no specific agent provided, check if user is admin for any agent
    if (!agentId) return permissions.agentAdminRoles.length > 0;
    
    // Check if user is admin for the specific agent
    return permissions.agentAdminRoles.some(role => role.agentId === agentId);
  }, [permissions]);

  // Check if user can manage a specific agent
  const canManageAgent = useCallback((agentId: string) => {
    if (!permissions) return false;
    
    // Super admins can manage all agents
    if (permissions.isSuperAdmin) return true;
    
    // Check if user is admin for this specific agent
    return permissions.agentAdminRoles.some(role => role.agentId === agentId);
  }, [permissions]);

  // Refresh permissions
  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    isLoading,
    error,
    hasAdminAccess,
    hasFullAdminAccess,
    isSuperAdmin,
    isAgentAdmin,
    canManageAgent,
    refreshPermissions,
  };
};
